import { GridColDef } from '@mui/x-data-grid-pro';
import { ApigeeApp } from '../../../../types';
import { useMemo, useState } from 'react';
import { UserKeys, getApiApps } from '../../../api/users-api';
import ApiGridToolbar from './api-toolbar';
import AddApiAppDialog from './add-api-app-dialog';
import DeleteApiAppDialog from './delete-api-app-dialog';
import { useQuery } from '@tanstack/react-query';
import { createGridOverlays } from '../../grid-overlay/no-data-overlay-helpers';
import { useUserSettings } from '../../../utils/use-user-settings';
import SharedDataGridClient from '../../../shared/shared-data-grid-client';
import { useToolbar } from '../../../shared/shared-data-grid-toolbar-helper';
import Apps from '@mui/icons-material/Apps';

interface ApiGridProps {
  changeLogRoles: string[];
  administrationRoles: string[];
}

export default function ApiGrid({
  changeLogRoles,
  administrationRoles,
}: ApiGridProps) {
  const [openCreateApiApp, setOpenCreateApiApp] = useState(false);
  const [openDeleteApiApp, setOpenDeleteApiApp] = useState(false);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);

  const {
    data: apiApps,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: [UserKeys.ApigeeApps],
    queryFn: () => getApiApps(),
    meta: {
      errorMessage:
        'There was an issue loading api app data. Please refresh and try again.',
    },
  });

  const columns: GridColDef<ApigeeApp>[] = useMemo(
    () => [
      {
        field: 'lastModifiedTime',
        headerName: 'Created Date',
        width: 185,
        type: 'dateTime',
        valueGetter: (value: number) => {
          return new Date(value);
        },
        valueFormatter: (date: Date) => {
          if (!date) {
            return 'Now';
          }
          return date.toLocaleString();
        },
      },
      {
        field: 'name',
        headerName: 'App Name',
        flex: 1,
        valueGetter: (value: string) => {
          return value;
        },
      },
      {
        field: 'description',
        headerName: 'Description',
        flex: 2,
        valueGetter: (value: string) => {
          return value;
        },
      },
      {
        field: 'developer',
        headerName: 'Developer',
        flex: 1,
        valueGetter: (value: string) => {
          return value;
        },
      },
      {
        field: 'appKey',
        headerName: 'Key',
        flex: 2,
        valueGetter: (value: string) => {
          return value;
        },
      },
    ],
    []
  );

  const { viewType } = useUserSettings();

  const defaultGridState = {
    sorting: {
      sortModel: [
        {
          field: 'createdDate',
          sort: 'desc',
        },
      ],
    },
  };

  const toolbarProps = useMemo(
    () => ({
      administrationRoles,
      setOpenCreateApiApp,
      setOpenDeleteApiApp,
      selectedRows,
    }),
    [administrationRoles, selectedRows]
  );

  return (
    <>
      <SharedDataGridClient
        slots={{
          toolbar: useToolbar(ApiGridToolbar, toolbarProps),
          ...createGridOverlays('Apps', <Apps />),
        }}
        defaultGridState={defaultGridState}
        gridKey={viewType.apisGridStates?.activeGridState}
        columns={columns}
        gridStateKey="apisGridStates"
        rows={apiApps || []}
        getRowId={(row) => row.appKey}
        loading={isLoading || isFetching}
        checkboxSelection
        selectedRows={selectedRows}
        setSelectedRows={setSelectedRows}
        sx={{
          '& .MuiDataGrid-row': {
            cursor: 'pointer',
          },
        }}
      />

      {openDeleteApiApp ? (
        <DeleteApiAppDialog
          open={openDeleteApiApp}
          handleClose={() => setOpenDeleteApiApp(false)}
          selectedRows={selectedRows}
          setSelectedRows={setSelectedRows}
          changeLogRoles={changeLogRoles}
        />
      ) : null}

      {openCreateApiApp ? (
        <AddApiAppDialog
          open={openCreateApiApp}
          handleClose={() => setOpenCreateApiApp(false)}
          changeLogRoles={changeLogRoles}
        />
      ) : null}
    </>
  );
}
